import _ from "lodash";
export const DATE_FORMAT = "YYYY-MM-DD HH:mm:ss";
export const SHORT_DATE_FORMAT = "MM-DD HH:mm";
export const SHORT_DATE_WITH_YEAR_FORMAT = "YY-M-DD HH:mm";
export const TIME_ONLY_FORMAT = "HH:mm";

export const CLOCK_DATE_FORMAT = "MMM D, YYYY";
export const CLOCK_DATE_FORMAT_MOBILE = "M/D/YY";

export const CLOCK_TIME_FORMAT = "hh:mm A";
export const CLOCK_TIME_FORMAT_MOBILE = "h:mm a";

export const CHART_TOOLTIP_FORMAT = "h:mm MMM Do";

export const ZERO_TIME = "0001-01-01 00:00:00";
export const AUDIO_ALERTS_ENABLED_TEXT = "Audio alerts enabled . !";
export const TESTING_AUDIO_TEXT = "Testing audio alerts";

export const GRID_VIEW = "grid";
export const SORT_BY_ROOM = "room";
export const FILTER_MY_PATIENTS = "myPatients";
export const UNIT_ALL = "All";

export const MIN_DATA_PATIENT = 6;

export const defaultSwiperSettings = {
  direction: "horizontal",
  // force the mouse to move a tiny bit, otherwise it can detect clicks as swipes
  threshold: 10,
  //space between slides to prevent shadows from bleeding between them
  spaceBetween: 10
};

export type LevelRange = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export enum Colors {
  Red = "#e61c24",
  Orange = "#ffa014",
  Green = "#87cd3c",
  Yellow = "#fce121",
  Gray = "#d2d2d2",
  LightGray = "#ededed",
  Black = "#000000",
  White = "#ffffff",
  DarkMode = "#4c4c4c",
  LightMode = "#fafafa",
  Blue = "#0071bc",
  LogoBlue = "#009edb",
  TestGreen = "#32cd32"
}

function getLevelColor(level: LevelRange, greenGrayOnly: boolean) {
  if (greenGrayOnly) {
    return getLevelColorPatient(level);
  } else {
    return getLevelColorNonPatient(level);
  }
}

function getLevelColorPatient(level: LevelRange) {
  // Convert to number
  const levelAsInt = +level;
  if (levelAsInt === 0) {
    return Colors.Gray;
  } else {
    return Colors.Green;
  }
}

function getLevelColorNonPatient(level: LevelRange) {
  // Convert to number
  const levelAsInt = +level;
  switch (levelAsInt) {
    case 5:
    case 1:
      return Colors.Red;
    case 2:
    case 4:
      return Colors.Yellow;
    case 3:
      return Colors.Green;
    case 6:
      return Colors.Orange;
    default:
      return Colors.Gray;
  }
}

function getLevelTableColor(level: LevelRange) {
  // Convert to number
  const levelAsInt = +level;
  switch (levelAsInt) {
    case 5:
    case 1:
      return "tableRed";
    case 2:
    case 4:
      return "tableYellow";
    case 3:
      return "tableGreen";
    default:
      return "";
  }
}

function getLevelIcon(level: LevelRange) {
  // Convert to number
  const levelAsInt = +level;

  switch (levelAsInt) {
    case 5:
    case 1:
      return "brightness_7";
    default:
      return "brightness_1";
  }
}

export function newWindowEventListeners(
  ref: any,
  name: string,
  roomSys: string = ""
) {
  window.addEventListener("beforeunload", event => {
    if (ref.editing === true) {
      // Cancel the event as stated by the standard.
      event.preventDefault();
      // Chrome requires returnValue to be set.
      event.returnValue = "";
      return "changed";
    } else {
      // the absence of a returnValue property on the event will guarantee the browser unload happens
      delete event["returnValue"];
    }
  });
  window.addEventListener("unload", function() {
    window.opener.awCommunicationChannel.releaseLockAndRefreshData(roomSys);
    window.opener.awCommunicationChannel.close(name, false);
  });
}

export function closeWindow(name: string) {
  window.opener.awCommunicationChannel.close(name, true);
}

// TODO: Get this from server
function getPercent(level: LevelRange): number {
  // Convert to number
  const levelAsInt = +level;

  switch (levelAsInt) {
    case 0:
      return 100;
    case 1:
      return 5;
    case 2:
      return 30;
    case 3:
      return 50;
    case 4:
      return 80;
    case 5:
      return 95;
    default:
      return 100;
  }
}

function chunkAndFill(
  inputArray: Array<any>,
  numberPerRow: number,
  fillValue = ""
) {
  if (inputArray.length === 0) {
    return inputArray;
  }
  const arrayOut = _.chunk(inputArray, numberPerRow);
  if (arrayOut.length === 0) {
    arrayOut[0] = [];
  }
  const lastRow = arrayOut[arrayOut.length - 1];
  const lengthOfLastRow = lastRow.length;

  for (let i = lengthOfLastRow; i < numberPerRow; i++) {
    lastRow.push(fillValue);
  }

  return arrayOut;
}

interface postMessageMessage {
  action: string;
  [index: string]: any;
}

export function postMessage(message: postMessageMessage) {
  const ReactNativeWebView = (window as any).ReactNativeWebView;
  if (ReactNativeWebView) {
    ReactNativeWebView.postMessage(JSON.stringify(message));
  }
}

export function addWindowDimensions(data: Record<string, any>) {
  if (!data["windowDim"]) {
    const height = window.innerHeight.toFixed(0);
    const width = window.innerWidth.toFixed(0);
    const windowDim = {
      height,
      width
    };
    data["windowDim"] = JSON.stringify(windowDim);
  }
}

const isArrayEqual = function(x: Array<any>, y: Array<any>) {
  return _.xorWith(x, y, _.isEqual).length === 0;
};

export function tryParseJSON(json: string | null, fallbackValue: any) {
  if (json === null) {
    return fallbackValue;
  }

  try {
    return JSON.parse(json);
  } catch {
    return fallbackValue;
  }
}

export interface AcuityLevel {
  text: string;
  value: number;
  color: Colors;
}

export const acuityLevels = [
  { text: "Not Set", value: 0, color: Colors.Gray },
  { text: "Stable", value: 1, color: Colors.Green },
  { text: "Watcher", value: 2, color: Colors.Yellow },
  { text: "Highest Risk", value: 3, color: Colors.Red },
  { text: "Discharge", value: 4, color: Colors.Blue }
];

// allows using await instead of using callback
// await animationFrame();
function animationFrame() {
  let resolve: FrameRequestCallback | null = null;
  const promise = new Promise(r => (resolve = r));
  if (resolve === null) {
    return promise;
  }
  window.requestAnimationFrame(resolve);
  return promise;
}

function refreshDataOnMainWindow() {
  // When no roomsys is passed only the data is refreshed, lock is kept
  window.opener.awCommunicationChannel.releaseLockAndRefreshData();
}

export default {
  animationFrame,
  getLevelColor,
  getLevelTableColor,
  getLevelIcon,
  getPercent,
  chunkAndFill,
  isArrayEqual,
  refreshDataOnMainWindow
};

export function getBrowserString(): string {
  const browser = getBrowser();
  return browser.name + " " + browser.version;
}

export function getBrowser(): any {
  let ua = navigator.userAgent,
    tem,
    M =
      ua.match(
        /(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i
      ) || [];
  if (/trident/i.test(M[1])) {
    tem = /\brv[ :]+(\d+)/g.exec(ua) || [];
    return { name: "IE", version: tem[1] || "" };
  }
  if (M[1] === "Chrome") {
    tem = ua.match(/\b(OPR|Edge)\/(\d+)/);
    if (tem != null) {
      return { name: tem[1].replace("OPR", "Opera"), version: tem[2] };
    }
  }
  M = M[2] ? [M[1], M[2]] : [navigator.appName, navigator.appVersion, "-?"];
  if ((tem = ua.match(/version\/(\d+)/i)) != null) {
    M.splice(1, 1, tem[1]);
  }
  return { name: M[0], version: M[1] };
}

export function supportsForeignObject() {
  return typeof SVGForeignObjectElement !== "undefined";
}

export function isBrowserCompatible() {
  return supportsForeignObject();
}
